import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Package, MapPin, Tag, BarChart3, User, Building } from 'lucide-react';
import { Item, ItemStatus, Location } from '@/types';
import { apiService } from '@/services/api';
import { demoService } from '@/services/demoService';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';

const CheckoutPage: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [isDemoModeEnabled, setIsDemoModeEnabled] = useState(false);
  const [selectedPlantNumber, setSelectedPlantNumber] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [locations, setLocations] = useState<Location[]>([]);

  useEffect(() => {
    const initializePage = async () => {
      if (!itemId) return;

      try {
        // Prüfe zuerst ob Demo-Modus aktiviert ist
        console.log('Checking demo mode status...');
        const demoModeEnabled = await demoService.isDemoModeEnabled();
        console.log('Demo mode enabled:', demoModeEnabled);
        setIsDemoModeEnabled(demoModeEnabled);

        if (demoModeEnabled) {
          // Demo-Modus aktiviert: Immer Demo-Modus verwenden wenn nicht authentifiziert
          if (!isAuthenticated) {
            console.log('Demo mode: Loading item without authentication');
            setDemoMode(true);
            await fetchDemoItem();
            return;
          } else {
            // Authentifiziert: Normaler Modus auch wenn Demo verfügbar ist
            console.log('Demo mode available but user is authenticated, using normal mode');
            setDemoMode(false);
            await fetchItem();
            return;
          }
        }

        // Kein Demo-Modus verfügbar
        if (isAuthenticated) {
          // Normaler Modus: Lade Item mit Authentifizierung
          console.log('Normal mode: Loading item with authentication');
          setDemoMode(false);
          await fetchItem();
        } else {
          // Kein Demo-Modus und nicht authentifiziert: Weiterleitung zum Login
          console.log('No demo mode and not authenticated, redirecting to login');
          toast.error('Bitte melden Sie sich an, um auf diese Seite zuzugreifen.');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error initializing page:', error);
        // Bei Fehler: Fallback auf normale Authentifizierung
        if (!isAuthenticated) {
          toast.error('Bitte melden Sie sich an, um auf diese Seite zuzugreifen.');
          navigate('/login');
        } else {
          await fetchItem();
        }
      }
    };

    initializePage();
  }, [itemId, isAuthenticated, navigate]);

  // Lade Standorte für Werknummern
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        let response;
        if (demoMode) {
          // Im Demo-Modus verwende öffentlichen Endpunkt
          response = await fetch('/api/system/public/locations/plant-numbers');
          const data = await response.json();
          setLocations(data.data || []);
        } else {
          // Normaler Modus: verwende authentifizierten Endpunkt
          response = await apiService.get<Location[]>('/locations');
          setLocations(response || []);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };

    if (isAuthenticated || demoMode) {
      fetchLocations();
    }
  }, [isAuthenticated, demoMode]);

  const fetchItem = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<Item>(`/items/${itemId}`);
      setItem(response);
    } catch (error: any) {
      console.error('Error fetching item:', error);
      
      if (error.response?.status === 401) {
        // Der apiService interceptor wird das automatisch behandeln
        return;
      } else {
        toast.error('Fehler beim Laden des Items');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDemoItem = async () => {
    try {
      setLoading(true);
      const response = await demoService.getDemoItemInfo(itemId!);
      setItem(response);
    } catch (error) {
      console.error('Error fetching demo item:', error);
      toast.error('Fehler beim Laden des Items');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: ItemStatus) => {
    const statusConfig = {
      [ItemStatus.AVAILABLE]: { variant: 'default' as const, label: 'Verfügbar' },
      [ItemStatus.CHECKED_OUT]: { variant: 'secondary' as const, label: 'Ausgeliehen' },
      [ItemStatus.MAINTENANCE]: { variant: 'destructive' as const, label: 'Wartung' },
      [ItemStatus.RETIRED]: { variant: 'outline' as const, label: 'Außer Betrieb' },
    };

    const config = statusConfig[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };



  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item) return;
    
    if (!isAuthenticated && !demoMode) {
      toast.error('Bitte melden Sie sich an, um Items auszuleihen.');
      navigate('/login');
      return;
    }

    // Validierung für SAP-geführte Items
    if (item.isManageInSAP && !selectedPlantNumber) {
      toast.error('Bitte wählen Sie eine Werknummer für dieses SAP-geführte Item aus.');
      return;
    }

    try {
      setCheckoutLoading(true);
      
      let notesText = demoMode 
        ? `Demo ${item.isRental ? 'Ausleihe' : 'Entnahme'}` 
        : `QR-Code ${item.isRental ? 'Ausleihe' : 'Entnahme'}`;
      
      // Füge Kommentar hinzu, falls vorhanden
      if (comments.trim()) {
        notesText += ` - ${comments.trim()}`;
      }
      
      console.log('Starting checkout with:', {
        itemId: item.id,
        type: 'CHECKOUT',
        quantity: quantity,
        notes: notesText,
        sapPlantNumber: selectedPlantNumber,
        demoMode
      });
      
      let result;
      if (demoMode) {
        // Demo-Modus: Verwende Demo-Service mit SAP-Werknummer
        result = await demoService.createDemoCheckout(item.id, quantity, notesText, selectedPlantNumber);
      } else {
        // Normaler Modus: Verwende API-Service
        const transactionData: any = {
          itemId: item.id,
          type: 'CHECKOUT',
          quantity: quantity,
          notes: notesText
        };

        // Füge SAP-Werknummer hinzu, falls das Item SAP-geführt ist
        if (item.isManageInSAP && selectedPlantNumber) {
          transactionData.sapPlantNumber = selectedPlantNumber;
        }

        result = await apiService.post('/transactions', transactionData);
      }
      
      console.log('Checkout successful:', result);

      toast.success(`Item erfolgreich ${item.isRental ? 'ausgeliehen' : 'entnommen'}!`);
      
      // Aktualisiere die Item-Daten
      if (demoMode) {
        await fetchDemoItem();
      } else {
        await fetchItem();
      }
      
      // Optional: Weiterleitung zur Items-Liste
      setTimeout(() => {
        navigate('/items');
      }, 2000);
      
    } catch (error: any) {
      console.error('Checkout error:', error);
      
      // Detaillierte Fehlerbehandlung
      if (error.response?.status === 401) {
        // Der apiService interceptor wird das automatisch behandeln
        return;
      } else if (error.response?.data?.error) {
        toast.error(`Fehler: ${error.response.data.error}`);
      } else if (error.message) {
        toast.error(`Fehler: ${error.message}`);
      } else {
        toast.error('Fehler beim Ausleihen des Items');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Item nicht gefunden</h2>
          <p className="text-muted-foreground mb-4">Das angeforderte Item existiert nicht oder wurde gelöscht.</p>
          <Button onClick={() => navigate('/items')}>Zurück zur Übersicht</Button>
        </div>
      </div>
    );
  }

  const actionText = item?.isRental ? 'ausleihen' : 'entnehmen';


  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Item {actionText}</h1>
        <p className="text-muted-foreground">
          {demoMode ? 'Demo-Modus: ' : ''}
          Scannen Sie den QR-Code, um dieses Item zu {actionText}
        </p>
        {demoMode && (
          <div className="mt-2">
            <Badge variant="secondary" className="bg-orange-100 text-orange-800">
              🎯 Demo-Modus aktiv
            </Badge>
          </div>
        )}
      </div>

      {/* Item Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {item.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-lg">{item.name}</h3>
                {item.description && (
                  <p className="text-muted-foreground">{item.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="text-muted-foreground">Mat. Nr:</span> {item.sku || '-'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="text-muted-foreground">Status:</span> {getStatusBadge(item.status)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="text-muted-foreground">Standort:</span> {item.location?.name || '-'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2">Bestand</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Aktuell:</span>
                    <span className="font-medium">{item.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Minimum:</span>
                    <span className="font-medium">{item.minQuantity}</span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checkout Form */}
      {item.status === 'AVAILABLE' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Item {actionText}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <strong>Angemeldet als:</strong> {demoMode ? 'Demo Benutzer (demo@example.com)' : `${user?.name} (${user?.email})`}
            </div>
            
            <form onSubmit={handleCheckout} className="space-y-4">
              <div>
                <label htmlFor="quantity" className="block text-sm font-medium mb-1">
                  Menge {actionText}
                </label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  max={item.quantity}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Verfügbar: {item.quantity} Stück
                </p>
              </div>

              {/* Debug Info */}
              <div style={{background: 'yellow', padding: '10px', margin: '10px 0'}}>
                <strong>DEBUG:</strong><br/>
                isManageInSAP: {String(item.isManageInSAP)}<br/>
                demoMode: {String(demoMode)}<br/>
                locations.length: {locations.length}<br/>
                locationsWithPlantNumber: {locations.filter(l => l.plantNumber).length}<br/>
                shouldShow: {String(item.isManageInSAP)}<br/>
                item.name: {item.name}
              </div>

              {/* SAP Werknummer Auswahl - nur für SAP-geführte Items */}
              {item.isManageInSAP && (
                <div>
                  <Label htmlFor="plantNumber" className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Werknummer (erforderlich) *
                  </Label>
                  <select
                    id="plantNumber"
                    value={selectedPlantNumber}
                    onChange={(e) => setSelectedPlantNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm mt-1"
                    required
                  >
                    <option value="">Bitte Werknummer auswählen...</option>
                    {locations
                      .filter(location => location.plantNumber)
                      .map(location => (
                        <option key={location.id} value={location.plantNumber}>
                          Werk {location.plantNumber} - {location.name}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Für SAP-geführte Items ist die Angabe einer Werknummer erforderlich.
                  </p>
                </div>
              )}

              {/* Kommentarfeld */}
              <div>
                <Label htmlFor="comments">
                  Kommentar (optional)
                </Label>
                <Textarea
                  id="comments"
                  placeholder="Optionaler Kommentar zur Entnahme..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Zusätzliche Informationen zur Entnahme (z.B. Verwendungszweck, Projekt)
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={checkoutLoading || quantity > item.quantity}
                  className="flex-1"
                >
                  {checkoutLoading 
                    ? `Wird ${item.isRental ? 'ausgeliehen' : 'entnommen'}...` 
                    : `${demoMode ? 'Demo: ' : ''}Item ${actionText}`
                  }
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/items')}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Item not available */}
      {item.status !== 'AVAILABLE' && (
        <Card>
          <CardContent className="text-center py-8">
            <h3 className="text-lg font-semibold mb-2">Item nicht verfügbar</h3>
            <p className="text-muted-foreground mb-4">
              Dieses Item kann derzeit nicht {item.isRental ? 'ausgeliehen' : 'entnommen'} werden.
            </p>
            <Button onClick={() => navigate('/items')}>
              Zurück zur Übersicht
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CheckoutPage;
